/**
 * Backend → SPA contract adapters (P2 drift reconciliation).
 *
 * The real API responds with the shapes in backend `src/web/src/lib/adapters.ts`;
 * the screens consume the scoped shapes in `contract.ts`. This module is the ONE
 * place that maps between them — fields the backend does not persist yet
 * (product phase, craClass, reportStatus, fixAvailableAt, markets-per-event…)
 * get honest defaults here and move server-side in P4.
 */
import type { Art14Row, Portfolio, ProductRow, TaskRow, WarningRow } from './contract';

// ─── Backend shapes (subset we consume) ──────────────────────────────────────
interface ApiVersionRow {
  id: string;
  label: string;
  placedOnMarket: boolean;
  releaseStatus?: string;
  sbomStatus: 'complete' | 'indeterminate' | 'pending' | 'failed' | 'none';
  componentCount: number;
  sbomQuality: number | null;
  /** Real counts from the read model (P4 landed) — fall back to 0 when absent. */
  openFindings?: number;
  art14Open?: number;
}
interface ApiProductRow {
  id: string;
  name: string;
  description: string | null;
  ownerUserRef: string | null;
  markets: string[];
  autoApproveFp: boolean;
  /** P4: now persisted server-side — fall back to defaults when absent. */
  phase?: ProductRow['phase'];
  craClass?: ProductRow['craClass'];
  versions: ApiVersionRow[];
}
export interface ApiPortfolio {
  families: Array<{ id: string; name: string }>;
  products: ApiProductRow[];
  unassigned: Array<{ id: string; name: string }>;
}
export interface ApiTaskRow {
  id: string;
  title: string;
  source: 'warning' | 'art14_event' | 'manual';
  status: 'todo' | 'in_progress' | 'done';
  assigneeUserRef: string | null;
  art14EventId: string | null;
  productId: string | null;
  versionId: string | null;
  createdAt: string;
  productName: string | null;
  cveId: string | null;
}
export interface ApiArt14Row {
  id: string;
  status: 'open' | 'notified' | 'overdue';
  /** Full stage domain — '1m' is the severe-incident final stage. */
  milestone: Art14Row['milestone'];
  detectedAt: string;
  enisaDeadline: string;
  followUpDeadline: string;
  finalReportDeadline: string;
  notifiedAt: string | null;
  followUpSubmittedAt: string | null;
  finalReportSubmittedAt: string | null;
  acknowledgedAt: string | null;
  impact: string | null;
  cveId: string;
  cvss: number | null;
  epss: number | null;
  isKev: boolean;
  component: string | null;
  versionLabel: string;
  productName: string;
  productId?: string;
  assigneeUserRef: string | null;
  vexStatus: string | null;
  vexJustification: string | null;
  vexJustificationCode?: string | null;
  vexImpact?: string | null;
  vexRemediation?: string | null;
  /** Removal state (Archived/deleted view). */
  archivedAt?: string | null;
  deletedAt?: string | null;
  notApplicableAt?: string | null;
  /** P4/P5: now persisted server-side — fall back to derivation when absent. */
  kind?: Art14Row['kind'];
  reportStatus?: Art14Row['reportStatus'];
  usersInformedAt?: string | null;
  fixAvailableAt?: string | null;
  /** User-entered Q16 values keyed by field id (sync store). */
  q16Overrides?: Record<string, string>;
}
export interface ApiWarningRow {
  findingId: string;
  cveId: string;
  component: string;
  componentVersion: string | null;
  severity: string | null;
  cvss: number | null;
  epss: number | null;
  isKev: boolean;
  applicability: string;
  productName: string | null;
  versionLabel: string | null;
  /** P3: version-scoped filtering keys. */
  versionId?: string;
  productId?: string;
  sources: string[];
  vexStatus: string | null;
}

// ─── Mappers ─────────────────────────────────────────────────────────────────

export function mapPortfolio(api: ApiPortfolio): Portfolio {
  const rows = api.products.map((p): ProductRow => ({
    id: p.id,
    name: p.name,
    description: p.description,
    ownerUserRef: p.ownerUserRef,
    markets: p.markets ?? [],
    autoApproveFp: p.autoApproveFp,
    phase: p.phase ?? 'active', // persisted in P4; default until the field lands
    craClass: p.craClass ?? 'default',
    versions: p.versions.map((v) => ({ ...v, release: v.placedOnMarket, releaseStatus: (v.releaseStatus ?? (v.placedOnMarket ? 'release' : 'internal')) as 'release' | 'rc' | 'beta' | 'alpha' | 'internal', openFindings: v.openFindings ?? 0, art14Open: v.art14Open ?? 0 })),
    openFindings: p.versions.reduce((n, v) => n + (v.openFindings ?? 0), 0),
    art14Open: p.versions.reduce((n, v) => n + (v.art14Open ?? 0), 0),
  }));
  // Products listed only under `unassigned` (no family) still must appear.
  const seen = new Set(rows.map((r) => r.id));
  for (const u of api.unassigned) {
    if (!seen.has(u.id)) {
      rows.push({
        id: u.id, name: u.name, description: null, ownerUserRef: null, markets: [],
        autoApproveFp: false, phase: 'active', craClass: 'default', versions: [],
        openFindings: 0, art14Open: 0,
      });
    }
  }
  return { products: rows };
}

export function mapTask(t: ApiTaskRow): TaskRow {
  const nav = t.art14EventId
    ? `/app/incidents/${t.art14EventId}`
    : t.productId && t.versionId
      ? `/app/products/${t.productId}/analysis/${t.versionId}`
      : t.productId
        ? `/app/products/${t.productId}`
        : '/app/today';
  return {
    id: t.id,
    title: t.title,
    source: t.source,
    status: t.status,
    assigneeUserRef: t.assigneeUserRef,
    productName: t.productName,
    cveId: t.cveId,
    due: null,
    hot: t.source === 'art14_event',
    nav,
  };
}

export function mapArt14(e: ApiArt14Row): Art14Row {
  return {
    id: e.id,
    // P5 persists kind (manual severe-incident create); else it's a vulnerability.
    kind: e.kind ?? 'vulnerability',
    status: e.status,
    milestone: e.milestone,
    cveId: e.cveId,
    cvss: e.cvss,
    epss: e.epss,
    isKev: e.isKev,
    component: e.component,
    versionLabel: e.versionLabel,
    productName: e.productName,
    productId: e.productId,
    assigneeUserRef: e.assigneeUserRef,
    detectedAt: e.detectedAt,
    fixAvailableAt: e.fixAvailableAt ?? null,
    markets: [],
    acknowledgedAt: e.acknowledgedAt,
    notifiedAt: e.notifiedAt,
    followUpSubmittedAt: e.followUpSubmittedAt,
    finalReportSubmittedAt: e.finalReportSubmittedAt,
    usersInformedAt: e.usersInformedAt ?? null,
    q16Overrides: e.q16Overrides ?? {},
    // The active VEX for this event's (version, vuln) — so the report can SHOW the
    // recorded determination instead of the panel reopening blank (user report 2026-07-20).
    vexStatus: e.vexStatus ?? null,
    vexJustification: e.vexJustification ?? null,
    vexJustificationCode: e.vexJustificationCode ?? null,
    vexImpact: e.vexImpact ?? null,
    vexRemediation: e.vexRemediation ?? null,
    archivedAt: e.archivedAt ?? null,
    deletedAt: e.deletedAt ?? null,
    notApplicableAt: e.notApplicableAt ?? null,
    reportStatus:
      e.reportStatus ??
      (e.milestone === 'complete' || e.finalReportSubmittedAt
        ? 'Sent'
        : e.acknowledgedAt
          ? 'Triaged'
          : 'New'),
  };
}

export function mapWarning(w: ApiWarningRow): WarningRow {
  const applicability =
    w.applicability === 'applicable' || w.applicability === 'not_applicable' || w.applicability === 'uncertain'
      ? w.applicability
      : ('uncertain' as const);
  return {
    findingId: w.findingId,
    cveId: w.cveId || null,
    component: w.component,
    componentVersion: w.componentVersion,
    severity: w.severity,
    cvss: w.cvss,
    epss: w.epss,
    isKev: w.isKev,
    applicability,
    productName: w.productName,
    versionLabel: w.versionLabel,
    versionId: w.versionId,
    productId: w.productId,
    sources: w.sources ?? [],
    vexStatus: w.vexStatus,
  };
}
